import {
  useCallback,
  useEffect,
  useMemo,
  useRef,
  useState,
  type ChangeEvent,
  type ReactNode,
} from "react";
import { api, ApiError } from "../lib/api";
import type { Asset, AssetFolder } from "../../shared/types";
import { useCan } from "../context/AuthContext";
import "../styles/asset-library.css";

// The B4 shared asset library: one library per product. Folders and single files
// each carry a "Partners" share toggle (violet) that exposes them to affiliates
// (node P4 consumes the flag). Everything here is wired to S4's live R2-backed
// asset API: real listing, real upload, real swipe copy, real share PATCH, real
// delete, real authorized download / preview. No mocks.

// ── small format helpers (local: brand assets are never downscaled) ──────────

export function typeLabel(type: Asset["type"]): string {
  if (type === "video") return "Video";
  if (type === "image") return "Image";
  if (type === "copy") return "Copy";
  return "File";
}

export function extOf(asset: Asset): string {
  const fn = asset.filename ?? "";
  const dot = fn.lastIndexOf(".");
  if (dot >= 0) return fn.slice(dot + 1).toLowerCase();
  const sub = asset.contentType?.split("/")[1];
  return sub ? sub.toLowerCase() : "";
}

function humanSize(bytes: number | null): string {
  if (bytes == null) return "";
  if (bytes < 1024) return `${bytes} B`;
  if (bytes < 1024 * 1024) return `${Math.round(bytes / 1024)} KB`;
  const mb = bytes / (1024 * 1024);
  return `${mb < 10 ? mb.toFixed(1) : Math.round(mb)} MB`;
}

/** The mono sub-line under an asset name: "png 512 KB" for files, a word count for copy. */
export function metaSub(asset: Asset): string {
  if (asset.type === "copy") {
    const words = (asset.text ?? "").trim().split(/\s+/).filter(Boolean).length;
    return `${words} ${words === 1 ? "word" : "words"}`;
  }
  return [extOf(asset), humanSize(asset.sizeBytes)].filter(Boolean).join(" · ");
}

// ── icons ────────────────────────────────────────────────────────────────────

function FolderIcon() {
  return (
    <svg className="fsvg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 7a2 2 0 0 1 2-2h4l2 2h8a2 2 0 0 1 2 2v8a2 2 0 0 1-2 2H5a2 2 0 0 1-2-2z" />
    </svg>
  );
}

function ThumbGlyph({ type }: { type: Asset["type"] }) {
  if (type === "video") {
    return (
      <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
        <path d="M9 8l7 4-7 4z" />
      </svg>
    );
  }
  if (type === "image") {
    return (
      <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
        <rect x="3.5" y="5" width="17" height="14" rx="2" />
        <circle cx="9" cy="10" r="1.6" />
        <path d="M5 17l4.5-4 3.5 3 3-2.5L19 17" />
      </svg>
    );
  }
  return (
    <svg className="tg" viewBox="0 0 24 24" aria-hidden="true">
      <path d="M7 3h7l4 4v14a1 1 0 0 1-1 1H7a1 1 0 0 1-1-1V4a1 1 0 0 1 1-1z" />
      <path d="M14 3v4h4" />
    </svg>
  );
}

function TrashIcon() {
  return (
    <svg viewBox="0 0 24 24" aria-hidden="true">
      <path d="M3 6h18" />
      <path d="M8 6V4a1 1 0 0 1 1-1h6a1 1 0 0 1 1 1v2" />
      <path d="M19 6l-1 14a2 2 0 0 1-2 2H8a2 2 0 0 1-2-2L5 6" />
      <path d="M10 11v6M14 11v6" />
    </svg>
  );
}

function SearchIcon() {
  return (
    <svg className="s-ic" viewBox="0 0 24 24" aria-hidden="true">
      <circle cx="11" cy="11" r="7" />
      <path d="M20.5 20.5l-4-4" />
    </svg>
  );
}

// ── modal shell ────────────────────────────────────────────────────────────

function Scrim({
  label,
  onClose,
  children,
  wide,
}: {
  label: string;
  onClose: () => void;
  children: ReactNode;
  wide?: boolean;
}) {
  useEffect(() => {
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") onClose();
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [onClose]);
  return (
    <div
      className="al-scrim"
      onMouseDown={(e) => {
        if (e.target === e.currentTarget) onClose();
      }}
    >
      <div className={`al-modal ${wide ? "wide" : ""}`} role="dialog" aria-modal="true" aria-label={label}>
        {children}
      </div>
    </div>
  );
}

// ── the share toggle (folder + asset), the core partner-exposure control ─────

function ShareToggle({
  on,
  busy,
  onToggle,
  title,
}: {
  on: boolean;
  busy: boolean;
  onToggle: () => void;
  title: string;
}) {
  const may = useCan()("admin");
  return (
    <button
      type="button"
      className={`share-tog ${on ? "on" : ""}`}
      disabled={busy || !may}
      aria-pressed={on}
      title={title}
      onClick={(e) => {
        e.stopPropagation();
        onToggle();
      }}
    >
      Partners
    </button>
  );
}

// ── the library's data and every change to it, for the page and for the phone ──

/** How a note after a change reads: a plain step, a success, or a failure. */
export type AssetNote = "info" | "good" | "warn";

/**
 * A product's whole library (its folders and every asset, with their share flags) and every change the
 * owner makes to it: share toggles, delete, a new folder, an upload, swipe copy written or changed.
 */
export function useAssetLibrary(productId: string, say: (msg: string, note?: AssetNote) => void) {
  const [folders, setFolders] = useState<AssetFolder[]>([]);
  const [assets, setAssets] = useState<Asset[]>([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState<string | null>(null);
  const [busy, setBusy] = useState<Set<string>>(new Set());

  const markBusy = useCallback((id: string, on: boolean) => {
    setBusy((prev) => {
      const next = new Set(prev);
      if (on) next.add(id);
      else next.delete(id);
      return next;
    });
  }, []);

  const load = useCallback(async () => {
    const d = await api.assets.list(productId);
    setFolders(d.folders);
    setAssets(d.assets);
  }, [productId]);

  useEffect(() => {
    let alive = true;
    setLoading(true);
    setErr(null);
    api.assets
      .list(productId)
      .then((d) => {
        if (!alive) return;
        setFolders(d.folders);
        setAssets(d.assets);
      })
      .catch((e: unknown) => alive && setErr(e instanceof ApiError ? e.message : String(e)))
      .finally(() => alive && setLoading(false));
    return () => {
      alive = false;
    };
  }, [productId]);

  // ── share toggles (optimistic then reconcile, revert on failure) ──

  const toggleFolderShare = useCallback(
    async (f: AssetFolder) => {
      if (busy.has(f.id)) return;
      const next = !f.sharedWithPartners;
      markBusy(f.id, true);
      setFolders((prev) => prev.map((x) => (x.id === f.id ? { ...x, sharedWithPartners: next } : x)));
      try {
        const res = await api.assets.patchFolder(f.id, { sharedWithPartners: next });
        setFolders((prev) => prev.map((x) => (x.id === f.id ? res.folder : x)));
      } catch (e) {
        setFolders((prev) =>
          prev.map((x) => (x.id === f.id ? { ...x, sharedWithPartners: f.sharedWithPartners } : x)),
        );
        say(`Couldn't update ${f.name}: ${e instanceof ApiError ? e.message : String(e)}`, "warn");
      } finally {
        markBusy(f.id, false);
      }
    },
    [busy, markBusy, say],
  );

  const toggleAssetShare = useCallback(
    async (a: Asset) => {
      if (busy.has(a.id)) return;
      const next = !a.sharedWithPartners;
      markBusy(a.id, true);
      setAssets((prev) => prev.map((x) => (x.id === a.id ? { ...x, sharedWithPartners: next } : x)));
      try {
        const res = await api.assets.patch(a.id, { sharedWithPartners: next });
        setAssets((prev) => prev.map((x) => (x.id === a.id ? res.asset : x)));
      } catch (e) {
        setAssets((prev) =>
          prev.map((x) => (x.id === a.id ? { ...x, sharedWithPartners: a.sharedWithPartners } : x)),
        );
        say(`Couldn't update ${a.name}: ${e instanceof ApiError ? e.message : String(e)}`, "warn");
      } finally {
        markBusy(a.id, false);
      }
    },
    [busy, markBusy, say],
  );

  // ── delete (hard: also drops the stored file; no restore endpoint, so confirm first) ──

  const doDelete = useCallback(
    async (a: Asset) => {
      markBusy(a.id, true);
      setAssets((prev) => prev.filter((x) => x.id !== a.id));
      try {
        await api.assets.delete(a.id);
        // Keep folder counts honest without a full refetch flash.
        setFolders((prev) =>
          prev.map((f) =>
            f.id === a.folderId ? { ...f, itemCount: Math.max(0, f.itemCount - 1) } : f,
          ),
        );
        say(`${a.name} deleted`, "info");
      } catch (e) {
        await load();
        say(`Couldn't delete ${a.name}: ${e instanceof ApiError ? e.message : String(e)}`, "warn");
      } finally {
        markBusy(a.id, false);
      }
    },
    [markBusy, say, load],
  );

  // ── create: folder, upload, swipe copy ──

  const createFolder = useCallback(
    async (name: string, shared: boolean) => {
      const res = await api.assets.createFolder(productId, { name, sharedWithPartners: shared });
      setFolders((prev) => [...prev, res.folder]);
      say(`Folder "${res.folder.name}" added`, "good");
    },
    [productId, say],
  );

  const upload = useCallback(
    async (file: File, folderId: string | null) => {
      const tempId = `up_${Date.now()}`;
      markBusy(tempId, true);
      say(`Uploading ${file.name}...`, "info");
      try {
        const res = await api.assets.upload(productId, file, { folderId });
        setAssets((prev) => [...prev, res.asset]);
        if (res.asset.folderId) {
          setFolders((prev) =>
            prev.map((f) => (f.id === res.asset.folderId ? { ...f, itemCount: f.itemCount + 1 } : f)),
          );
        }
        say(`${res.asset.name} uploaded`, "good");
      } catch (uErr) {
        say(`Couldn't upload ${file.name}: ${uErr instanceof ApiError ? uErr.message : String(uErr)}`, "warn");
      } finally {
        markBusy(tempId, false);
      }
    },
    [productId, markBusy, say],
  );

  const createCopy = useCallback(
    async (name: string, text: string, shared: boolean, folderId: string | null) => {
      const res = await api.assets.createCopy(productId, {
        name,
        text,
        folderId,
        sharedWithPartners: shared,
      });
      setAssets((prev) => [...prev, res.asset]);
      if (res.asset.folderId) {
        setFolders((prev) =>
          prev.map((f) => (f.id === res.asset.folderId ? { ...f, itemCount: f.itemCount + 1 } : f)),
        );
      }
      say(`${res.asset.name} added`, "good");
    },
    [productId, say],
  );

  const saveCopyEdit = useCallback(
    async (id: string, name: string, text: string) => {
      const res = await api.assets.patch(id, { name, text });
      setAssets((prev) => prev.map((x) => (x.id === id ? res.asset : x)));
      say("Swipe copy saved", "good");
    },
    [say],
  );

  const copyToClipboard = useCallback(
    async (a: Asset) => {
      try {
        await navigator.clipboard.writeText(a.text ?? "");
        say("Copied to clipboard", "good");
      } catch {
        say("Couldn't copy. Open Edit to select the text.", "warn");
      }
    },
    [say],
  );

  return {
    folders,
    assets,
    loading,
    err,
    busy,
    toggleFolderShare,
    toggleAssetShare,
    doDelete,
    createFolder,
    upload,
    createCopy,
    saveCopyEdit,
    copyToClipboard,
  };
}

// ── main ─────────────────────────────────────────────────────────────────────

type AddState = null | "choose" | "copy";
type EditState = { id: string; name: string; text: string } | null;

const NOTE_COLOR: Record<AssetNote, string> = { info: "var(--fire)", good: "var(--win)", warn: "var(--warn)" };

export function AssetLibrary({ productId }: { productId: string }) {
  const may = useCan()("admin");
  const [openFolder, setOpenFolder] = useState<{ id: string; name: string } | null>(null);
  const [query, setQuery] = useState("");
  const [searchOpen, setSearchOpen] = useState(false);

  // modals
  const [showFolderModal, setShowFolderModal] = useState(false);
  const [add, setAdd] = useState<AddState>(null);
  const [edit, setEdit] = useState<EditState>(null);
  const [preview, setPreview] = useState<Asset | null>(null);
  const [confirmDel, setConfirmDel] = useState<Asset | null>(null);

  const fileRef = useRef<HTMLInputElement>(null);
  const searchRef = useRef<HTMLInputElement>(null);

  const [toast, setToast] = useState<{ msg: string; color: string; show: boolean }>({
    msg: "",
    color: "var(--fire)",
    show: false,
  });
  const toastTimer = useRef<number | null>(null);
  const showToast = useCallback((msg: string, color = "var(--fire)") => {
    setToast({ msg, color, show: true });
    if (toastTimer.current) window.clearTimeout(toastTimer.current);
    toastTimer.current = window.setTimeout(() => setToast((t) => ({ ...t, show: false })), 2800);
  }, []);
  useEffect(() => () => void (toastTimer.current && window.clearTimeout(toastTimer.current)), []);
  const say = useCallback((msg: string, note: AssetNote = "info") => showToast(msg, NOTE_COLOR[note]), [showToast]);

  const {
    folders,
    assets,
    loading,
    err,
    busy,
    toggleFolderShare,
    toggleAssetShare,
    doDelete,
    createFolder,
    upload,
    createCopy,
    saveCopyEdit,
    copyToClipboard,
  } = useAssetLibrary(productId, say);

  const q = query.trim().toLowerCase();
  const searching = q.length > 0;

  // What renders: searching = matching assets across the whole library; a folder is
  // open = that folder's assets; root = unfoldered assets. Folders show only at root.
  const shownAssets = useMemo(() => {
    if (searching) return assets.filter((a) => a.name.toLowerCase().includes(q));
    if (openFolder) return assets.filter((a) => a.folderId === openFolder.id);
    return assets.filter((a) => a.folderId === null);
  }, [assets, searching, q, openFolder]);

  const showFolders = !searching && !openFolder;
  const showAddTile = !searching;
  const targetFolderId = openFolder?.id ?? null;

  const onUploadPick = (e: ChangeEvent<HTMLInputElement>) => {
    const file = e.target.files?.[0];
    e.target.value = "";
    if (file) void upload(file, targetFolderId);
  };

  // ── render ──

  return (
    <div className="assetlib">
      <p className="setnote">
        One library for this product. Turn on{" "}
        <b className="v">Partners</b> on a folder or a single file to share it with your affiliates.
      </p>

      <div className="assetbox">
        <div className="assetnav">
          <div className="crumb">
            <button
              type="button"
              className="cr-root"
              onClick={() => {
                setOpenFolder(null);
                setQuery("");
              }}
            >
              All assets
            </button>
            {openFolder && !searching && (
              <>
                <span className="cr-sep">/</span>
                <span className="cr-cur">{openFolder.name}</span>
              </>
            )}
          </div>
          <div className="nav-right">
            <div className={`searchbox ${searchOpen || searching ? "open" : ""}`}>
              <button
                type="button"
                className="s-btn"
                aria-label="Search assets"
                onClick={() => {
                  setSearchOpen(true);
                  searchRef.current?.focus();
                }}
              >
                <SearchIcon />
              </button>
              <input
                ref={searchRef}
                className="asset-search"
                placeholder="Search assets"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                onFocus={() => setSearchOpen(true)}
                onBlur={() => {
                  if (!query.trim()) setSearchOpen(false);
                }}
              />
            </div>
            {showFolders && may && (
              <button
                type="button"
                className="btn ghost sm newfolderbtn"
                onClick={() => setShowFolderModal(true)}
              >
                + New folder
              </button>
            )}
          </div>
        </div>

        {loading && <div className="al-note">Loading library...</div>}
        {!loading && err && <div className="al-note">Couldn't load the library: {err}</div>}

        {!loading && !err && (
          <div className="assets">
            {showFolders &&
              folders.map((f) => (
                <div
                  key={f.id}
                  className="folder-row"
                  role="button"
                  tabIndex={0}
                  onClick={() => setOpenFolder({ id: f.id, name: f.name })}
                  onKeyDown={(e) => {
                    if (e.key === "Enter" || e.key === " ") {
                      e.preventDefault();
                      setOpenFolder({ id: f.id, name: f.name });
                    }
                  }}
                >
                  <span className="fic">
                    <FolderIcon />
                  </span>
                  <div className="fmeta">
                    <div className="fn">{f.name}</div>
                    <div className="fc">
                      {f.itemCount} {f.itemCount === 1 ? "item" : "items"}
                    </div>
                  </div>
                  <ShareToggle
                    on={f.sharedWithPartners}
                    busy={busy.has(f.id)}
                    onToggle={() => toggleFolderShare(f)}
                    title={
                      f.sharedWithPartners
                        ? "Shared with partners. Every file inside is visible to your affiliates."
                        : "Private. Turn on to share this whole folder with partners."
                    }
                  />
                  <span className="fchev" aria-hidden="true">
                    &#8250;
                  </span>
                </div>
              ))}

            {shownAssets.map((a) => (
              <div className="asset" key={a.id}>
                <div className={`thumb ${a.type === "copy" ? "c" : ""}`}>
                  <span className="ty">{typeLabel(a.type)}</span>
                  {a.type === "copy" ? (
                    <span className="ctext">{a.text}</span>
                  ) : (
                    <ThumbGlyph type={a.type} />
                  )}
                </div>
                <div className="meta">
                  <div className="an" title={a.name}>
                    {a.name}
                  </div>
                  <div className="as">{metaSub(a)}</div>
                </div>
                <ShareToggle
                  on={a.sharedWithPartners}
                  busy={busy.has(a.id)}
                  onToggle={() => toggleAssetShare(a)}
                  title={
                    a.partnerVisible && !a.sharedWithPartners
                      ? "Visible to partners through its shared folder."
                      : a.sharedWithPartners
                        ? "Shared with partners."
                        : "Private. Turn on to share this file with partners."
                  }
                />
                <div className="acts">
                  {a.type === "copy" ? (
                    <>
                      <button type="button" className="btn ghost sm" onClick={() => copyToClipboard(a)}>
                        Copy
                      </button>
                      {may && (
                        <button
                          type="button"
                          className="btn ghost sm"
                          onClick={() => setEdit({ id: a.id, name: a.name, text: a.text ?? "" })}
                        >
                          Edit
                        </button>
                      )}
                    </>
                  ) : (
                    // U0: the prototype shows a "Replace" action on media assets. S4
                    // has no replace endpoint (replacing would be delete + re-upload,
                    // which changes the asset id and drops its share state), so B4
                    // ships the scoped Preview + Download instead. Confirm whether a
                    // real in-place Replace is wanted (needs an S4 route that
                    // overwrites the deterministic R2 key). See GRAPH-v2 section 9.
                    <>
                      {(a.type === "image" || a.type === "video") && (
                        <button type="button" className="btn ghost sm" onClick={() => setPreview(a)}>
                          Preview
                        </button>
                      )}
                      <a className="btn ghost sm" href={api.assets.downloadUrl(a.id)}>
                        Download
                      </a>
                    </>
                  )}
                  {may && (
                    <button
                      type="button"
                      className="del"
                      aria-label={`Delete ${a.name}`}
                      title="Delete"
                      disabled={busy.has(a.id)}
                      onClick={() => setConfirmDel(a)}
                    >
                      <TrashIcon />
                    </button>
                  )}
                </div>
              </div>
            ))}

            {showAddTile && may && (
              <button type="button" className="asset add" onClick={() => setAdd("choose")}>
                <span className="plus">+</span>
                <span>Add asset{openFolder ? ` to ${openFolder.name}` : ""}</span>
              </button>
            )}

            {/* Honest empty states */}
            {!showFolders && shownAssets.length === 0 && (
              <div className="al-empty">
                {searching ? `No assets match "${query.trim()}".` : "No assets in this folder yet."}
              </div>
            )}
            {showFolders && folders.length === 0 && shownAssets.length === 0 && (
              <div className="al-empty">No assets yet. Add a file or swipe copy, or make a folder.</div>
            )}
          </div>
        )}
      </div>

      <input
        ref={fileRef}
        type="file"
        className="al-file"
        onChange={onUploadPick}
        aria-hidden="true"
      />

      {showFolderModal && (
        <FolderModal
          onClose={() => setShowFolderModal(false)}
          onCreate={createFolder}
          onToast={showToast}
        />
      )}

      {add === "choose" && (
        <Scrim label="Add asset" onClose={() => setAdd(null)}>
          <div className="mhead">
            <h3>Add asset</h3>
            <button type="button" className="mx" aria-label="Close" onClick={() => setAdd(null)}>
              &#10005;
            </button>
          </div>
          <div className="mbody">
            <p className="mnote">
              {openFolder ? (
                <>
                  Adding to <b>{openFolder.name}</b>.
                </>
              ) : (
                "Adding to the library root. Move it into a folder any time."
              )}
            </p>
            <div className="add-choices">
              <button
                type="button"
                className="add-choice"
                onClick={() => {
                  setAdd(null);
                  fileRef.current?.click();
                }}
              >
                <span className="ac-ic up" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M12 16V4M7 9l5-5 5 5" />
                    <path d="M4 15v3a2 2 0 0 0 2 2h12a2 2 0 0 0 2-2v-3" />
                  </svg>
                </span>
                <span className="ac-t">Upload a file</span>
                <span className="ac-s">Video, image, or document. Stored on R2.</span>
              </button>
              <button type="button" className="add-choice" onClick={() => setAdd("copy")}>
                <span className="ac-ic copy" aria-hidden="true">
                  <svg viewBox="0 0 24 24">
                    <path d="M5 5h11v14H5z" />
                    <path d="M8 5V3h11v14h-2" />
                  </svg>
                </span>
                <span className="ac-t">Write swipe copy</span>
                <span className="ac-s">Captions or email text partners can copy.</span>
              </button>
            </div>
          </div>
        </Scrim>
      )}

      {add === "copy" && (
        <CopyModal
          title="New swipe copy"
          submitLabel="Add copy"
          onClose={() => setAdd(null)}
          onSubmit={async (name, text, shared) => {
            await createCopy(name, text, shared, targetFolderId);
            setAdd(null);
          }}
          onToast={showToast}
        />
      )}

      {edit && (
        <CopyModal
          title="Edit swipe copy"
          submitLabel="Save"
          initialName={edit.name}
          initialText={edit.text}
          hideShare
          onClose={() => setEdit(null)}
          onSubmit={async (name, text) => {
            await saveCopyEdit(edit.id, name, text);
            setEdit(null);
          }}
          onToast={showToast}
        />
      )}

      {preview && <PreviewModal asset={preview} onClose={() => setPreview(null)} />}

      {confirmDel && (
        <Scrim label="Delete asset" onClose={() => setConfirmDel(null)}>
          <div className="mhead">
            <h3>Delete this asset?</h3>
            <button type="button" className="mx" aria-label="Close" onClick={() => setConfirmDel(null)}>
              &#10005;
            </button>
          </div>
          <div className="mbody">
            <p className="mnote">
              <b>{confirmDel.name}</b> will be removed for good.
              {confirmDel.type !== "copy" && " This deletes the stored file, so partners lose access too."}{" "}
              This can't be undone.
            </p>
            <div className="mfoot">
              <button type="button" className="btn ghost" onClick={() => setConfirmDel(null)}>
                Cancel
              </button>
              <button
                type="button"
                className="btn danger"
                onClick={() => {
                  setConfirmDel(null);
                  void doDelete(confirmDel);
                }}
              >
                Delete
              </button>
            </div>
          </div>
        </Scrim>
      )}

      <div className={`al-toast ${toast.show ? "show" : ""}`} role="status" aria-live="polite">
        <span className="tdot" style={{ background: toast.color }} />
        <span className="msg">{toast.msg}</span>
      </div>
    </div>
  );
}

// ── new-folder modal ─────────────────────────────────────────────────────────

function FolderModal({
  onClose,
  onCreate,
  onToast,
}: {
  onClose: () => void;
  onCreate: (name: string, shared: boolean) => Promise<void>;
  onToast: (msg: string, color?: string) => void;
}) {
  const [name, setName] = useState("");
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const valid = name.trim().length > 0;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onCreate(name.trim(), shared);
      onClose();
    } catch (e) {
      setSaving(false);
      onToast(`Couldn't create the folder: ${e instanceof ApiError ? e.message : String(e)}`, "var(--warn)");
    }
  };

  return (
    <Scrim label="New folder" onClose={onClose}>
      <div className="mhead">
        <h3>New folder</h3>
        <button type="button" className="mx" aria-label="Close" onClick={onClose}>
          &#10005;
        </button>
      </div>
      <div className="mbody">
        <label className="flabel" htmlFor="al-folder-name">
          Folder name
        </label>
        <input
          id="al-folder-name"
          className="inp"
          value={name}
          autoFocus
          placeholder="Social"
          onChange={(e) => setName(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter") submit();
          }}
        />
        <button
          type="button"
          className={`share-row ${shared ? "on" : ""}`}
          aria-pressed={shared}
          onClick={() => setShared((s) => !s)}
        >
          <span className="share-tog inline">Partners</span>
          <span className="share-copy">
            {shared ? "Every file inside is shared with partners." : "Keep private for now."}
          </span>
          <span className={`switch ${shared ? "on" : ""}`} aria-hidden="true">
            <span className="knob" />
          </span>
        </button>
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={submit} disabled={!valid || saving}>
            {saving ? "Adding..." : "Add folder"}
          </button>
        </div>
      </div>
    </Scrim>
  );
}

// ── swipe-copy create / edit modal ───────────────────────────────────────────

function CopyModal({
  title,
  submitLabel,
  initialName = "",
  initialText = "",
  hideShare,
  onClose,
  onSubmit,
  onToast,
}: {
  title: string;
  submitLabel: string;
  initialName?: string;
  initialText?: string;
  hideShare?: boolean;
  onClose: () => void;
  onSubmit: (name: string, text: string, shared: boolean) => Promise<void>;
  onToast: (msg: string, color?: string) => void;
}) {
  const [name, setName] = useState(initialName);
  const [text, setText] = useState(initialText);
  const [shared, setShared] = useState(false);
  const [saving, setSaving] = useState(false);
  const valid = name.trim().length > 0 && text.trim().length > 0;

  const submit = async () => {
    if (!valid || saving) return;
    setSaving(true);
    try {
      await onSubmit(name.trim(), text, shared);
    } catch (e) {
      setSaving(false);
      onToast(`Couldn't save: ${e instanceof ApiError ? e.message : String(e)}`, "var(--warn)");
    }
  };

  return (
    <Scrim label={title} onClose={onClose} wide>
      <div className="mhead">
        <h3>{title}</h3>
        <button type="button" className="mx" aria-label="Close" onClick={onClose}>
          &#10005;
        </button>
      </div>
      <div className="mbody">
        <label className="flabel" htmlFor="al-copy-name">
          Name
        </label>
        <input
          id="al-copy-name"
          className="inp"
          value={name}
          autoFocus
          placeholder="Launch email"
          onChange={(e) => setName(e.target.value)}
        />
        <label className="flabel" htmlFor="al-copy-text">
          Text
        </label>
        <textarea
          id="al-copy-text"
          className="inp ta"
          value={text}
          rows={6}
          placeholder="The caption or email your partners can copy and paste."
          onChange={(e) => setText(e.target.value)}
        />
        {!hideShare && (
          <button
            type="button"
            className={`share-row ${shared ? "on" : ""}`}
            aria-pressed={shared}
            onClick={() => setShared((s) => !s)}
          >
            <span className="share-tog inline">Partners</span>
            <span className="share-copy">
              {shared ? "Shared with partners." : "Keep private for now."}
            </span>
            <span className={`switch ${shared ? "on" : ""}`} aria-hidden="true">
              <span className="knob" />
            </span>
          </button>
        )}
        <div className="mfoot">
          <button type="button" className="btn ghost" onClick={onClose} disabled={saving}>
            Cancel
          </button>
          <button type="button" className="btn" onClick={submit} disabled={!valid || saving}>
            {saving ? "Saving..." : submitLabel}
          </button>
        </div>
      </div>
    </Scrim>
  );
}

// ── preview lightbox: fetches the authorized bytes and renders inline ────────

function PreviewModal({ asset, onClose }: { asset: Asset; onClose: () => void }) {
  const [url, setUrl] = useState<string | null>(null);
  const [state, setState] = useState<"loading" | "ready" | "error">("loading");
  const [msg, setMsg] = useState("");

  useEffect(() => {
    let alive = true;
    let objectUrl: string | null = null;
    setState("loading");
    fetch(api.assets.downloadUrl(asset.id), { credentials: "include" })
      .then(async (res) => {
        if (!res.ok) {
          throw new ApiError(res.status, res.status === 404 ? "This file's bytes are not in storage." : res.statusText);
        }
        const blob = await res.blob();
        if (!alive) return;
        objectUrl = URL.createObjectURL(blob);
        setUrl(objectUrl);
        setState("ready");
      })
      .catch((e: unknown) => {
        if (!alive) return;
        setMsg(e instanceof ApiError ? e.message : String(e));
        setState("error");
      });
    return () => {
      alive = false;
      if (objectUrl) URL.revokeObjectURL(objectUrl);
    };
  }, [asset.id]);

  const isPdf = asset.contentType === "application/pdf" || extOf(asset) === "pdf";

  return (
    <Scrim label={`Preview ${asset.name}`} onClose={onClose} wide>
      <div className="mhead">
        <h3>{asset.name}</h3>
        <button type="button" className="mx" aria-label="Close" onClick={onClose}>
          &#10005;
        </button>
      </div>
      <div className="mbody preview">
        {state === "loading" && <div className="al-note">Loading preview...</div>}
        {state === "error" && (
          <div className="al-empty">
            {msg} You can still download it to view.
            <div className="mfoot" style={{ justifyContent: "flex-start", marginTop: 12 }}>
              <a className="btn ghost sm" href={api.assets.downloadUrl(asset.id)}>
                Download
              </a>
            </div>
          </div>
        )}
        {state === "ready" && url && asset.type === "image" && (
          <img className="pv-img" src={url} alt={asset.name} />
        )}
        {state === "ready" && url && asset.type === "video" && (
          <video className="pv-video" src={url} controls>
            <track kind="captions" />
          </video>
        )}
        {state === "ready" && url && asset.type === "file" && isPdf && (
          <iframe className="pv-frame" src={url} title={asset.name} />
        )}
        {state === "ready" && url && asset.type === "file" && !isPdf && (
          <div className="al-empty">
            No inline preview for this file type.
            <div className="mfoot" style={{ justifyContent: "flex-start", marginTop: 12 }}>
              <a className="btn ghost sm" href={api.assets.downloadUrl(asset.id)}>
                Download
              </a>
            </div>
          </div>
        )}
      </div>
    </Scrim>
  );
}
